/**
 * EntityTypesPage — "أنواع الجهات" management (iter-218g, Feb 2026).
 *
 * The user's Payment Entities form (/dashboard/finance/settings/payment-entities)
 * now requires an "Entity Type" that comes from this shared dropdown list.
 * Seeded on backend startup with 16 default categories (Software subscriptions,
 * Cloud/Hosting/Domains, AI tools, Marketing, Legal, Accounting, etc.).
 *
 * Route: /control-panel-settings/dropdowns/entity-types
 *
 * Structurally mirrors JobTitlesPage so the two admin dropdown pages stay
 * visually consistent (add form with AI translate on both sides + list with
 * inline edit/delete).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { List, Plus, Edit3, Trash2, Check, X, Loader2, Languages, Tag } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EntityTypesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { token } = useAuth();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  // Reachable from Finance → Settings → Dropdown Lists (?from=finance) and from Control-Panel Settings.
  const fromFinance = searchParams.get('from') === 'finance';
  const backPath = fromFinance ? '/dashboard/finance/settings/dropdown-lists' : '/control-panel-settings/dropdowns';
  const breadcrumb = fromFinance
    ? [
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'إعدادات القسم المالي', labelEn: 'Finance Settings', path: '/dashboard/finance/settings' },
        { labelAr: 'القوائم المنسدلة', labelEn: 'Dropdown Lists', path: '/dashboard/finance/settings/dropdown-lists' },
        { labelAr: 'أنواع الجهات', labelEn: 'Entity Types' },
      ]
    : [
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'القوائم المنسدلة', labelEn: 'Dropdown Lists', path: '/control-panel-settings/dropdowns' },
        { labelAr: 'أنواع الجهات', labelEn: 'Entity Types' },
      ];

  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(true);
  const [newRow, setNewRow] = useState({ name_ar: '', name_en: '' });
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [translatingField, setTranslatingField] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const autoTranslate = async ({ text, source, target, fieldKey, applyFn }) => {
    if (!text || !text.trim()) return;
    setTranslatingField(fieldKey);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/utils/translate`,
        { text: text.trim(), source, target },
        { headers },
      );
      const translated = (res.data?.translated || '').trim();
      if (translated) applyFn(translated);
    } catch {
      toast.error(t('فشل في الترجمة التلقائية', 'Auto-translate failed'));
    } finally {
      setTranslatingField(null);
    }
  };

  const fetchRows = async () => {
    setLoadingRows(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/dropdowns/entity-types`, { headers });
      setRows(res.data.data || []);
    } catch {
      toast.error(t('فشل في جلب الأنواع', 'Failed to fetch entity types'));
    } finally {
      setLoadingRows(false);
    }
  };

  useEffect(() => { fetchRows(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async () => {
    if (!newRow.name_ar || !newRow.name_en) {
      toast.error(t('يرجى ملء جميع الحقول', 'Please fill all fields'));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/dropdowns/entity-types`, newRow, { headers });
      toast.success(t('تمت الإضافة', 'Entity type added'));
      setNewRow({ name_ar: '', name_en: '' });
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error adding entity type');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editingRow.name_ar || !editingRow.name_en) {
      toast.error(t('يرجى ملء جميع الحقول', 'Please fill all fields'));
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/dropdowns/entity-types/${id}`, editingRow, { headers });
      toast.success(t('تم التحديث', 'Entity type updated'));
      setEditingRow(null);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error updating entity type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('حذف هذا النوع؟', 'Delete this entity type?'))) return;
    setDeletingId(id);
    try {
      await axios.delete(`${BACKEND_URL}/api/dropdowns/entity-types/${id}`, { headers });
      toast.success(t('تم الحذف', 'Deleted'));
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting entity type');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <DashboardLayout
      title={t('أنواع الجهات', 'Entity Types')}
      titleEn="Entity Types"
      subtitle={t('إدارة قائمة أنواع الجهات المستخدمة في نموذج جهات الدفع',
                  'Manage entity types used in the Payment Entities form')}
      subtitleEn="Manage entity types used in the Payment Entities form"
      screenCode="DDL-003"
      backPath={backPath}
      onBack={() => navigate(backPath)}
      breadcrumb={breadcrumb}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="entity-types-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Add form */}
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
              <Plus className="h-5 w-5 text-emerald-400" />
              {t('إضافة نوع جديد', 'Add New Entity Type')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
              <div className="space-y-2">
                <label className="h-5 flex items-center text-sm text-[#A1A1AA]">
                  {t('الاسم بالعربية', 'Name in Arabic')}
                </label>
                <Input
                  value={newRow.name_ar}
                  onChange={(e) => setNewRow({ ...newRow, name_ar: e.target.value })}
                  placeholder={t('مثال: اشتراكات البرمجيات والأنظمة', 'Example: اشتراكات البرمجيات والأنظمة')}
                  className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                  dir="rtl"
                  data-testid="new-entity-type-ar"
                />
                <button
                  type="button"
                  onClick={() => autoTranslate({
                    text: newRow.name_ar,
                    source: 'ar',
                    target: 'en',
                    fieldKey: 'new_en',
                    applyFn: (v) => setNewRow((p) => ({ ...p, name_en: v })),
                  })}
                  disabled={!newRow.name_ar.trim() || translatingField === 'new_en'}
                  className="w-full h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-cyan-500/20 transition disabled:opacity-40"
                  data-testid="entity-type-translate-ar-to-en"
                >
                  {translatingField === 'new_en'
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Languages className="h-3.5 w-3.5" />}
                  {t('ترجمة إلى الإنجليزية', 'Translate to English')}
                </button>
              </div>
              <div className="space-y-2">
                <label className="h-5 flex items-center text-sm text-[#A1A1AA]">
                  {t('الاسم بالإنجليزية', 'Name in English')}
                </label>
                <Input
                  value={newRow.name_en}
                  onChange={(e) => setNewRow({ ...newRow, name_en: e.target.value })}
                  placeholder="Example: Software & Systems Subscriptions"
                  className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                  data-testid="new-entity-type-en"
                />
                <button
                  type="button"
                  onClick={() => autoTranslate({
                    text: newRow.name_en,
                    source: 'en',
                    target: 'ar',
                    fieldKey: 'new_ar',
                    applyFn: (v) => setNewRow((p) => ({ ...p, name_ar: v })),
                  })}
                  disabled={!newRow.name_en.trim() || translatingField === 'new_ar'}
                  className="w-full h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-cyan-500/20 transition disabled:opacity-40"
                  data-testid="entity-type-translate-en-to-ar"
                >
                  {translatingField === 'new_ar'
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Languages className="h-3.5 w-3.5" />}
                  {t('ترجمة إلى العربية', 'Translate to Arabic')}
                </button>
              </div>
            </div>
            <p className="text-xs text-[#606060] mt-2">
              {t('💡 اكتب الاسم بإحدى اللغتين ثم اضغط زر الترجمة — يمكن التعديل قبل الحفظ.',
                 '💡 Type the name in either language then click the translate button — editable before save.')}
            </p>
            <Button
              onClick={handleAdd}
              disabled={saving}
              className="mt-4 bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-entity-type-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : <Plus className="h-4 w-4 ml-2" />}
              {t('إضافة', 'Add')}
            </Button>
          </div>

          {/* List */}
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
              <List className="h-5 w-5 text-[#d4af37]" />
              {t('الأنواع الحالية', 'Current Entity Types')}
              <span className="text-sm font-normal text-[#71717A]">({rows.length})</span>
            </h3>

            {loadingRows ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-[#d4af37]" />
              </div>
            ) : rows.length === 0 ? (
              <p className="text-center text-[#71717A] py-8">
                {t('لا توجد أنواع بعد', 'No entity types yet')}
              </p>
            ) : (
              <div className="space-y-3">
                {rows.map((row) => (
                  <div
                    key={row.id}
                    className="flex items-center justify-between p-4 bg-white/[0.02] rounded-xl border border-white/5"
                    data-testid={`entity-type-row-${row.id}`}
                  >
                    {editingRow && editingRow.id === row.id ? (
                      <div className="flex-1 flex items-center gap-3">
                        <Input
                          value={editingRow.name_ar}
                          onChange={(e) => setEditingRow({ ...editingRow, name_ar: e.target.value })}
                          className="bg-white/5 border-white/10 text-white flex-1"
                          dir="rtl"
                        />
                        <Input
                          value={editingRow.name_en}
                          onChange={(e) => setEditingRow({ ...editingRow, name_en: e.target.value })}
                          className="bg-white/5 border-white/10 text-white flex-1"
                        />
                        <Button
                          size="sm"
                          onClick={() => handleUpdate(row.id)}
                          disabled={saving}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingRow(null)}
                          className="text-[#71717A] hover:text-white"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#d4af37]/10 border border-[#d4af37]/30 shrink-0">
                            <Tag className="h-4 w-4 text-[#d4af37]" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate">{row.name_ar}</p>
                            <p className="text-[#71717A] text-sm truncate">{row.name_en}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingRow(row)}
                            className="text-[#71717A] hover:text-[#d4af37]"
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(row.id)}
                            disabled={deletingId === row.id}
                            className="text-[#71717A] hover:text-red-400"
                          >
                            {deletingId === row.id
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </section>
    </DashboardLayout>
  );
}
